import React, { useEffect, useState, useRef } from 'react';
import { Home, Map, Settings, ChevronLeft, ChevronRight, Building2, Calendar, Sparkles, Receipt, ChevronsUpDown, Check, LogOut, User, AlertCircle, ClipboardList, Eye, X, ChevronDown } from 'lucide-react';
export type UserRole = 'cleaner' | 'host';
interface SidebarProps {
  isCollapsed: boolean;
  toggleCollapse: () => void;
  activePage: string;
  onNavigate: (page: string) => void;
  userRole: UserRole;
  onUserRoleChange: (role: UserRole) => void;
  isMobileOpen?: boolean;
  setIsMobileOpen?: (isOpen: boolean) => void;
}
export function Sidebar({
  isCollapsed,
  toggleCollapse,
  activePage,
  onNavigate,
  userRole,
  onUserRoleChange,
  isMobileOpen = false,
  setIsMobileOpen
}: SidebarProps) {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isPlannerExpanded, setIsPlannerExpanded] = useState(activePage === 'planner' || activePage === 'planner-map');
  const profileRef = useRef<HTMLDivElement>(null);
  // Keep planner expanded when navigating to planner sub-pages
  useEffect(() => {
    if (activePage === 'planner' || activePage === 'planner-map') {
      setIsPlannerExpanded(true);
    }
  }, [activePage]);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const cleanerNavItems = [{
    id: 'dashboard',
    icon: Home,
    label: 'Panoramica'
  }, {
    id: 'planner',
    icon: ClipboardList,
    label: 'Pianificatore',
    subItems: [{
      id: 'planner',
      icon: Calendar,
      label: 'Calendario'
    }, {
      id: 'planner-map',
      icon: Map,
      label: 'Mappa Operativa'
    }]
  }, {
    id: 'team',
    icon: User,
    label: 'La Mia Squadra'
  }, {
    id: 'billing',
    icon: Receipt,
    label: 'Fatturazione'
  }, {
    id: 'settings',
    icon: Settings,
    label: 'Impostazioni'
  }];
  const hostNavItems = [{
    id: 'host-properties',
    icon: Building2,
    label: 'Le Mie Proprietà'
  }, {
    id: 'host-esigenze',
    icon: AlertCircle,
    label: 'Esigenze'
  }, {
    id: 'host-monitoraggio',
    icon: Eye,
    label: 'Monitoraggio'
  }, {
    id: 'host-calendario',
    icon: Calendar,
    label: 'Calendario'
  }, {
    id: 'host-billing',
    icon: Receipt,
    label: 'Fatturazione'
  }, {
    id: 'host-settings',
    icon: Settings,
    label: 'Impostazioni'
  }];
  const navItems = userRole === 'cleaner' ? cleanerNavItems : hostNavItems;
  const isActive = (itemId: string) => {
    if (activePage === itemId) return true;
    // Handle sub-pages
    if (activePage.startsWith(itemId + '-')) return true;
    // Special case for packages detail view
    if (itemId === 'packages' && activePage === 'package-detail') return true;
    // Special case for planner parent matching planner-map
    if (itemId === 'planner' && activePage === 'planner-map') return true;
    return false;
  };
  const handleRoleSwitch = (role: UserRole) => {
    onUserRoleChange(role);
    setIsProfileOpen(false);
    // Navigate to default page for that role
    if (role === 'cleaner') {
      onNavigate('dashboard');
    } else {
      onNavigate('host-properties');
    }
  };
  const handleNavigate = (page: string) => {
    onNavigate(page);
    if (setIsMobileOpen) {
      setIsMobileOpen(false);
    }
  };
  return <>
      {/* Mobile Overlay */}
      {isMobileOpen && <div className="fixed inset-0 bg-black/50 z-[55] lg:hidden backdrop-blur-sm transition-opacity" onClick={() => setIsMobileOpen && setIsMobileOpen(false)} data-id="element-2271" />}

      {/* Sidebar Container */}
      <aside className={`
        fixed top-0 bottom-0 bg-white border-r border-gray-200 transition-all duration-300 z-[60] flex flex-col
        ${isMobileOpen ? 'translate-x-0 w-64' : '-translate-x-full lg:translate-x-0'}
        ${isCollapsed ? 'lg:w-16' : 'lg:w-64'}
      `} data-id="element-2272">
        {/* Logo Area */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200" data-id="element-2273">
          <div className="flex items-center justify-start overflow-hidden w-full" data-id="element-2274">
            {isCollapsed && !isMobileOpen ? <img src="https://cdn.magicpatterns.com/uploads/cJxCKhK8JLpfPAGZaUVBXZ/logoC.png" alt="CleanAI" className="h-9 w-9 object-contain" data-id="element-2275" /> : <img src={userRole === 'host' ? 'https://cdn.magicpatterns.com/uploads/c3CVXkfWdk86GemZunsFAx/cleanai_host.png' : 'https://cdn.magicpatterns.com/uploads/2rmZLDJmSv9wpG6sQ1XUir/cleanai_b2b.png'} alt={userRole === 'host' ? 'CleanAI Host' : 'CleanAI B2B'} className="h-9 object-contain transition-all duration-300" data-id="element-2276" />}
          </div>
          {/* Mobile Close Button */}
          <button onClick={() => setIsMobileOpen && setIsMobileOpen(false)} className="lg:hidden p-1 text-gray-500 hover:bg-gray-100 rounded-lg" data-id="element-2277">
            <X size={20} data-id="element-2278" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto" data-id="element-2279">
          {navItems.map(item => {
          const hasSubItems = 'subItems' in item && item.subItems;
          const isParentActive = isActive(item.id);
          return <div key={item.id} data-id="element-2280">
                <button onClick={() => {
              if (hasSubItems) {
                setIsPlannerExpanded(!isPlannerExpanded);
                if (!isParentActive) {
                  handleNavigate(item.subItems![0].id);
                }
              } else {
                handleNavigate(item.id);
              }
            }} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors group ${isParentActive ? 'bg-gray-50 text-navy border border-gray-200' : 'text-gray-600 hover:bg-gray-50 hover:text-navy border border-transparent'}`} title={isCollapsed ? item.label : undefined} data-id="element-2281">
                  <item.icon className={`w-4 h-4 flex-shrink-0 ${isParentActive ? 'text-teal-deep' : 'text-gray-500 group-hover:text-navy'}`} data-id="element-2282" />
                  {(!isCollapsed || isMobileOpen) && <>
                      <span className={`text-sm font-inter flex-1 text-left ${isParentActive ? 'font-medium' : ''}`} data-id="element-2283">
                        {item.label}
                      </span>
                      {hasSubItems && <ChevronDown size={14} className={`text-gray-400 transition-transform duration-200 ${isPlannerExpanded && isParentActive ? 'rotate-180' : ''}`} data-id="element-2284" />}
                    </>}
                </button>

                {/* Sub-items */}
                {hasSubItems && isPlannerExpanded && (!isCollapsed || isMobileOpen) && <div className="mt-1 ml-3 pl-4 border-l border-gray-200 space-y-0.5" data-id="element-2285">
                      {item.subItems!.map(sub => <button key={sub.id} onClick={() => handleNavigate(sub.id)} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors text-left ${activePage === sub.id ? 'text-teal-deep font-medium bg-teal-50/50' : 'text-gray-500 hover:text-navy hover:bg-gray-50'}`} data-id="element-2286">
                          <sub.icon size={14} className={activePage === sub.id ? 'text-teal-deep' : 'text-gray-400'} data-id="element-2287" />
                          <span className="text-xs font-inter" data-id="element-2288">
                            {sub.label}
                          </span>
                        </button>)}
                    </div>}
              </div>;
        })}
        </nav>

        {/* User Profile & Collapse Toggle */}
        <div className="p-3 border-t border-gray-200 bg-gray-50/50 relative" data-id="element-2289">
          {/* Profile Switcher */}
          <div ref={profileRef} className="relative mb-3" data-id="element-2290">
            {isProfileOpen && <div className={`absolute bottom-full mb-2 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden z-50 w-60 animate-in fade-in slide-in-from-bottom-2 duration-200 ${isCollapsed ? 'left-full ml-2' : 'left-0 w-full'}`} data-id="element-2291">
                <div className="p-2 space-y-1" data-id="element-2292">
                  <button onClick={() => handleRoleSwitch('cleaner')} className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${userRole === 'cleaner' ? 'bg-teal-50' : 'hover:bg-gray-50'}`} data-id="element-2293">
                    <div className="w-8 h-8 rounded-full bg-teal-100 border border-teal-200 flex items-center justify-center flex-shrink-0 text-teal-deep text-xs font-bold" data-id="element-2294">
                      SB
                    </div>
                    <div className="flex-1 text-left" data-id="element-2295">
                      <p className="text-sm font-medium text-navy" data-id="element-2296">
                        Sofia Bianchi
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2297">Manager Area</p>
                    </div>
                    {userRole === 'cleaner' && <Check size={16} className="text-teal-deep" data-id="element-2298" />}
                  </button>

                  <button onClick={() => handleRoleSwitch('host')} className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${userRole === 'host' ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-2299">
                    <div className="w-8 h-8 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center flex-shrink-0 text-blue-600 text-xs font-bold" data-id="element-2300">
                      MR
                    </div>
                    <div className="flex-1 text-left" data-id="element-2301">
                      <p className="text-sm font-medium text-navy" data-id="element-2302">
                        Mario Rossi
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2303">Host</p>
                    </div>
                    {userRole === 'host' && <Check size={16} className="text-blue-600" data-id="element-2304" />}
                  </button>
                </div>
                <div className="border-t border-gray-100 p-2" data-id="element-2305">
                  <button className="w-full flex items-center gap-2 p-2 rounded-lg text-gray-500 hover:bg-gray-50 hover:text-red-600 transition-colors text-xs font-medium" data-id="element-2306">
                    <LogOut size={14} data-id="element-2307" />
                    Esci
                  </button>
                </div>
              </div>}

            <button onClick={() => setIsProfileOpen(!isProfileOpen)} className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} p-2 rounded-lg hover:bg-white hover:shadow-sm transition-all border border-transparent hover:border-gray-200 group`} data-id="element-2308">
              {userRole === 'cleaner' ? <div className="w-8 h-8 rounded-full bg-teal-100 border border-teal-200 flex items-center justify-center flex-shrink-0" data-id="element-2309">
                  <span className="text-teal-deep text-xs font-bold" data-id="element-2310">SB</span>
                </div> : <div className="w-8 h-8 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center flex-shrink-0" data-id="element-2311">
                  <span className="text-blue-600 text-xs font-bold" data-id="element-2312">MR</span>
                </div>}

              {(!isCollapsed || isMobileOpen) && <>
                  <div className="flex-1 overflow-hidden text-left" data-id="element-2313">
                    <p className="text-sm font-medium text-navy truncate" data-id="element-2314">
                      {userRole === 'cleaner' ? 'Sofia Bianchi' : 'Mario Rossi'}
                    </p>
                    <p className="text-xs text-gray-500 truncate" data-id="element-2315">
                      {userRole === 'cleaner' ? 'Manager Area' : 'Host'}
                    </p>
                  </div>
                  <ChevronsUpDown size={14} className="text-gray-400 group-hover:text-navy" data-id="element-2316" />
                </>}
            </button>
          </div>

          <button onClick={toggleCollapse} className="w-full hidden lg:flex items-center justify-end p-2 rounded-lg hover:bg-gray-200 text-gray-500 transition-colors" aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} data-id="element-2317">
            {isCollapsed ? <ChevronRight size={16} data-id="element-2318" /> : <ChevronLeft size={16} data-id="element-2319" />}
          </button>
        </div>
      </aside>
    </>;
}